import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPortal } from "react-dom";
// Legacy import: import { useSelector, useDispatch } from "react-redux";
import "./Orders.scss";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import { SortIcon } from "../../assets/Icons/SortIcon";
import { ExportIcon } from "../../assets/Icons/ExportIcon";
import FilterFunnelIcon from "../../assets/Icons/FilterFunnelIcon";
import {
  OrdersFilterPanel,
  EMPTY_ORDER_FILTERS,
  filterOrdersByFilters,
  hasActiveOrderFilters,
} from "./OrdersFilterPanel";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import { InfoIcon } from "../../assets/Icons/InfoIcon";
import { CalendarIcon } from "../../assets/Icons/Calendar";
import { ClockIcon } from "../../assets/Icons/ClockIcon";
import { OutlookplusIcon } from "../../assets/Icons/OutlookplusIcon";
import { useNavigate } from "react-router-dom";
import { CustomFieldsTooltip } from "../CommonUIComponents/CustomFieldsTooltip/CustomFieldsTooltip";
import { Tooltip } from "../CommonUIComponents/Tooltip/Tooltip";
import { LoadingIndicator } from "../CommonUIComponents/LoadingIndicator/LoadingIndicator";
import { OrdersService } from "../../api/services/orders.service";
import { LookupService } from "../../api/services/lookup.service";
import { clearOrdersRefresh } from "../../store/slices/ordersSlice";
import { setAllowedOrderIds } from "../../store/slices/userSlice";
import { ORDERS, ORDER_FILTERS } from "../../util/constants";
import { createExcelFile } from "../../util/excelUtil";
import { CustomTooltipBottom } from "../CommonUIComponents/CustomTooltip/CustomTooltipBottom";
import { CustomTooltip } from "../CommonUIComponents/CustomTooltip/CustomTooltip";

const getPriorityRank = (priority) => {
  const normalizedPriority = String(priority || "")
    .trim()
    .toUpperCase();
  const match = normalizedPriority.match(/^P(\d+)$/);

  if (!match) {
    return Number.MAX_SAFE_INTEGER;
  }

  return Number(match[1]);
};

const getAgingValue = (agingIn) => {
  const numericValue = parseFloat(
    String(agingIn || "").replace(/[^\d.-]/g, ""),
  );
  return Number.isNaN(numericValue) ? Number.MAX_SAFE_INTEGER : numericValue;
};

const getDateValue = (dateString) => {
  const normalizedDateString = String(dateString || "")
    .trim()
    .replace(/(\d+)(st|nd|rd|th)/gi, "$1")
    .replace(/,/g, "");
  const timestamp = new Date(normalizedDateString).getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

const formatExportHeader = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatExportDate = (value) => {
  if (!value) {
    return "";
  }

  const normalizedDateString = String(value)
    .trim()
    .replace(/(\d+)(st|nd|rd|th)/gi, "$1")
    .replace(/,/g, "");
  const parsedDate = new Date(normalizedDateString);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const formatExportValue = (fieldName, value) => {
  if (value == null) {
    return "";
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  if (/date/i.test(fieldName)) {
    return formatExportDate(value);
  }

  return value;
};

const mapOrderToExportRow = (order = {}) =>
  Object.entries(order).reduce((row, [fieldName, value]) => {
    row[formatExportHeader(fieldName)] = formatExportValue(fieldName, value);
    return row;
  }, {});

const buildExportSheet = (sheetName, ordersList = []) => ({
  name: sheetName,
  data: ordersList.map(mapOrderToExportRow),
});

// Dashboard component displaying open orders and action needed items
export const OrdersDashboard = ({ isFullWidth = false }) => {
  const [activeButton, setActiveButton] = useState("openOrders");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState("Priority");
  const [searchTerm, setSearchTerm] = useState("");
  const [orders, setOrders] = useState({ openOrders: [], actionNeeded: [] });
  const [countryNamesById, setCountryNamesById] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 100 });
  const [activeFilter, setActiveFilter] = useState(null); // null means no filter active
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_ORDER_FILTERS);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Get selectedRole and rolesLoaded from Redux state
  const selectedRole = useSelector((state) => state.user?.selectedRole);
  const rolesLoaded = useSelector((state) => state.user?.rolesLoaded);
  const shouldRefreshOrders = useSelector(
    (state) => state.orders.shouldRefreshOrders,
  );
  const roleName =
    typeof selectedRole === "object"
      ? (selectedRole?.roleName || selectedRole?.name || "").toUpperCase()
      : String(selectedRole || "").toUpperCase();
  const isRegionalLead = roleName === "REGIONAL LEAD";

  // Track the last fetch parameters to prevent duplicate API calls
  const lastFetchParamsRef = useRef(null);

  // Fetches orders on component mount and pagination changes
  useEffect(() => {
    // Wait for roles to be loaded and roleId to be available before fetching orders
    // This prevents API calls during screen refresh or token expiry when role is not yet set
    if (!rolesLoaded || !selectedRole?.roleId) {
      return;
    }

    // Create a unique key for the actual fetch parameters (excluding shouldRefreshOrders)
    const fetchParamsKey = `${pagination.page}-${pagination.pageSize}-${selectedRole?.roleId || ""}`;

    // Skip if we just fetched with the same parameters and shouldRefreshOrders is false
    // This handles the case where shouldRefreshOrders changes from true to false after clearing
    if (lastFetchParamsRef.current === fetchParamsKey && !shouldRefreshOrders) {
      return;
    }
    lastFetchParamsRef.current = fetchParamsKey;

    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await OrdersService.getMyOrders({
          page: pagination.page,
          pageSize: pagination.pageSize,
          roleId: selectedRole?.roleId,
        });
        const ordersData = response?.data || response || {};
        const allOrders = [
          ...(ordersData.openOrders || []),
          ...(ordersData.actionNeeded || []),
        ];
        const countryIds = new Set(
          allOrders
            .map((order) => order?.countryId ?? order?.CountryId)
            .filter(
              (countryId) => countryId !== undefined && countryId !== null,
            )
            .map((countryId) => String(countryId).trim())
            .filter(Boolean),
        );
        let nextCountryNamesById = {};

        if (isRegionalLead) {
          const superRegionGuids = Array.from(
            new Set(
              allOrders
                .map((order) =>
                  String(
                    order?.superRegionGuId ??
                      order?.superRegionGuid ??
                      order?.SuperRegionGuid ??
                      "",
                  ).trim(),
                )
                .filter(Boolean),
            ),
          );

          // Resolve country IDs to names for the Regional Lead Region filter.
          const countryLookupResults = await Promise.allSettled(
            superRegionGuids.map((superRegionGuid) =>
              LookupService.getCountriesBySuperRegion(superRegionGuid),
            ),
          );

          countryLookupResults.forEach((result) => {
            if (result.status !== "fulfilled") {
              return;
            }

            const lookupResponse = result.value;
            const countries = Array.isArray(lookupResponse?.data)
              ? lookupResponse.data
              : Array.isArray(lookupResponse)
                ? lookupResponse
                : [];

            countries.forEach((country) => {
              const countryId = country?.countryId ?? country?.id;
              const countryName = String(
                country?.countryName ?? country?.name ?? "",
              ).trim();
              const normalizedCountryId = String(countryId ?? "").trim();

              if (countryIds.has(normalizedCountryId) && countryName) {
                nextCountryNamesById[normalizedCountryId] = countryName;
              }
            });
          });
        }

        setCountryNamesById(nextCountryNamesById);
        setOrders({
          openOrders: ordersData.openOrders || [],
          actionNeeded: ordersData.actionNeeded || [],
        });
      } catch (err) {
        setOrders({ openOrders: [], actionNeeded: [] });
        setCountryNamesById({});
        setError(ORDERS.FETCH_ERROR);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();

    // Clear the refresh flag after fetching
    if (shouldRefreshOrders) {
      dispatch(clearOrdersRefresh());
    }
    // Legacy dependency list: [userEmail, pagination.page, pagination.pageSize]
  }, [
    pagination.page,
    pagination.pageSize,
    shouldRefreshOrders,
    dispatch,
    selectedRole?.roleId,
    rolesLoaded,
    isRegionalLead,
  ]);

  const priorityOptions = [
    {
      label: (
        <>
          <SortIcon width={17} height={17} color="#737387" /> {ORDERS.SORT_BY}
        </>
      ),
      selectable: false,
    },
    { label: ORDERS.PRIORITY, selectable: true },
    { label: ORDERS.AGEING, selectable: true },
    { label: ORDERS.DUE_DATE, selectable: true },
  ];

  const handleButton1Click = () => {
    setActiveButton("openOrders");
    setActiveFilter(null);
  };

  // Switches to action needed view
  const handleButton2Click = () => {
    setActiveButton("actionNeeded");
    setActiveFilter(null);
  };

  // Toggles sort dropdown visibility
  const handleRightButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handles priority sort option selection
  const handlePrioritySelect = (option) => {
    if (option.selectable) {
      setSelectedPriority(option.label);
      setIsDropdownOpen(false);
    }
  };

  // Navigates to order details page with source information
  const handleOrderClick = (order, source = "openOrders") => {
    // Navigate immediately without waiting for AI orders API
    navigate(`/orders/${order.guid}`, {
      state: { orderDetails: order, source },
    });

    // Fetch AI-allowed order IDs in the background (non-blocking)
    if (selectedRole?.roleId) {
      OrdersService.getAIOrders(selectedRole.roleId)
        .then((aiOrdersData) => {
          const rawOrderIds = Array.isArray(aiOrdersData)
            ? aiOrdersData
            : aiOrdersData?.data?.orderIds || aiOrdersData?.orderIds || [];
          const orderIds = rawOrderIds.map((id) => String(id));
          dispatch(setAllowedOrderIds(orderIds));
        })
        .catch((aiErr) => {
          console.warn("Failed to fetch AI order IDs:", aiErr);
        });
    }
  };

  // Truncates text to specified length with ellipsis
  const truncateText = (text, maxLength = 25) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Renders text with tooltip if exceeds max length
  const renderWithTooltip = (text, maxLength = 35) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return (
      <CustomTooltipBottom
        trigger={
          <span className="truncated-text">
            {truncateText(text, maxLength)}
          </span>
        }
        normalText={text}
      />
    );
  };

  // Formats date to "MMM dd, yyyy" format
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Checks if order has outlook agent type (case-insensitive)
  const isOutlookAgent = (order) => {
    const agentType = order?.agentType || "";
    return agentType.toLowerCase() === "outlook_agent";
  };

  // Orders of the active tab, narrowed by the side-panel filters (Regional Lead only)
  const tabOrders = useMemo(
    () =>
      activeButton === "openOrders"
        ? orders?.openOrders || []
        : orders?.actionNeeded || [],
    [orders, activeButton],
  );

  const filterableTabOrders = useMemo(
    () =>
      tabOrders.map((order) => {
        const countryId = String(
          order?.countryId ?? order?.CountryId ?? "",
        ).trim();
        const countryName = countryNamesById[countryId];

        return countryName ? { ...order, country: countryName } : order;
      }),
    [tabOrders, countryNamesById],
  );

  const panelFilteredOrders = useMemo(
    () =>
      isRegionalLead
        ? filterOrdersByFilters(filterableTabOrders, appliedFilters)
        : tabOrders,
    [tabOrders, filterableTabOrders, appliedFilters, isRegionalLead],
  );

  // Calculates filter counts based on active button's orders
  const filterCounts = useMemo(() => {
    const currentOrders = panelFilteredOrders;

    const counts = {
      overdue: 0,
      dueThisWeek: 0,
      dataTriage: 0,
      dataTransformation: 0,
      dataLoading: 0,
    };

    currentOrders.forEach((order) => {
      if (order.isOverDueRequest === true) {
        counts.overdue++;
      }
      if (order.isDueThisWeekRequest === true) {
        counts.dueThisWeek++;
      }

      const status = (order.status || "").toLowerCase();
      if (order.isPendingDataTriageRequest === true) {
        counts.dataTriage++;
      }
      if (order.isPendingDataTransformationRequest === true) {
        counts.dataTransformation++;
      }
      if (order.isPendingDataLoadingRequest === true) {
        counts.dataLoading++;
      }
    });

    return counts;
  }, [panelFilteredOrders]);

  // Filters orders based on selected filter criteria
  const filteredOrders = useMemo(() => {
    const currentOrders = panelFilteredOrders;

    if (!activeFilter) {
      return currentOrders;
    }

    return currentOrders.filter((order) => {
      switch (activeFilter) {
        case "overdue":
          return order.isOverDueRequest === true;
        case "dueThisWeek":
          return order.isDueThisWeekRequest === true;
        case "dataTriage":
          return order.isPendingDataTriageRequest === true;
        case "dataTransformation":
          return order.isPendingDataTransformationRequest === true;
        case "dataLoading":
          return order.isPendingDataLoadingRequest === true;
        default:
          return true;
      }
    });
  }, [panelFilteredOrders, activeFilter]);

  const searchedOrders = useMemo(() => {
    // if (!isRegionalLead) {
    //   return filteredOrders;
    // }
    // console.log("Search :", filteredOrders);
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return filteredOrders;
    }

    return filteredOrders.filter((order) => {
      const searchableValues = [
        order.axonId,
        `AX-${order.axonId}`, // Also match full axonId format with "AX-" prefix
        order.clientEntity,
        order.analyzer,
        order.status,
        order.priority,
        order.engagementName,
        order.engagementCode,
        order.agingIn,
        order.dueDate,
      ];

      return searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(normalizedSearchTerm),
      );
    });
  }, [filteredOrders, isRegionalLead, searchTerm]);

  const sortedOrders = useMemo(() => {
    const nextOrders = [...searchedOrders];

    switch (selectedPriority) {
      case ORDERS.PRIORITY:
        return nextOrders.sort((leftOrder, rightOrder) => {
          const priorityDifference =
            getPriorityRank(leftOrder.priority) -
            getPriorityRank(rightOrder.priority);

          if (priorityDifference !== 0) {
            return priorityDifference;
          }

          return (
            getDateValue(leftOrder.dueDate) - getDateValue(rightOrder.dueDate)
          );
        });
      case ORDERS.AGEING:
        return nextOrders.sort(
          (leftOrder, rightOrder) =>
            getAgingValue(rightOrder.agingIn) -
            getAgingValue(leftOrder.agingIn),
        );
      case ORDERS.DUE_DATE:
        return nextOrders.sort(
          (leftOrder, rightOrder) =>
            getDateValue(leftOrder.dueDate) - getDateValue(rightOrder.dueDate),
        );
      default:
        return nextOrders;
    }
  }, [searchedOrders, selectedPriority]);

  const shouldShowOrderCounts = !isLoading;
  const hasOrdersToExport = isRegionalLead
    ? sortedOrders.length > 0
    : (orders?.openOrders || []).length > 0 ||
      (orders?.actionNeeded || []).length > 0;

  const handleExportOrders = async () => {
    if (isLoading || !hasOrdersToExport) {
      return;
    }

    try {
      const exportSheets = [
        buildExportSheet(
          activeButton === "openOrders" ? "Open Orders" : "Action Needed",
          sortedOrders,
        ),
      ];
      const exportBlob = await createExcelFile([...exportSheets]);

      if (!exportBlob) {
        return;
      }

      const downloadUrl = window.URL.createObjectURL(exportBlob);
      const link = document.createElement("a");
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[T:]/g, "-");

      link.href = downloadUrl;
      link.download = `orders-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (exportError) {
      console.error("Failed to export orders", exportError);
    }
  };

  // Toggles filter on/off for order list
  const handleFilterClick = (filterName) => {
    setActiveFilter((prev) => (prev === filterName ? null : filterName));
  };

  // Closes dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="orders-dashboard">
      {isRegionalLead &&
        isFilterPanelOpen &&
        createPortal(
          <OrdersFilterPanel
            orders={filterableTabOrders}
            appliedFilters={appliedFilters}
            onClose={() => setIsFilterPanelOpen(false)}
            onApply={(nextFilters) => {
              setAppliedFilters(nextFilters);
              setIsFilterPanelOpen(false);
            }}
          />,
          document.body,
        )}
      <div className="orders-dashboard-header">
        <div className="orders-dashboard-left">
          <button
            className={`orders-text-button ${
              activeButton === "openOrders" ? "active" : ""
            }`}
            onClick={handleButton1Click}
          >
            {ORDERS.OPEN_ORDERS}{" "}
            {shouldShowOrderCounts && (
              <p className={activeButton === "openOrders" ? "active" : ""}>
                {(orders?.openOrders || []).length}
              </p>
            )}
          </button>
          <button
            className={`orders-text-button ${
              activeButton === "actionNeeded" ? "active" : ""
            }`}
            onClick={handleButton2Click}
          >
            {ORDERS.ACTION_NEEDED}{" "}
            {shouldShowOrderCounts && (
              <p className={activeButton === "actionNeeded" ? "active" : ""}>
                {(orders?.actionNeeded || []).length}
              </p>
            )}
          </button>
        </div>
        <div className="orders-dashboard-right" ref={dropdownRef}>
          <div className="orders-search-container">
            <SearchIcon width={16} height={16} color="#737387" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="orders-search-input"
            />
          </div>
          {isRegionalLead && (
            <button
              type="button"
              className={`orders-icon-button orders-filter-button${hasActiveOrderFilters(appliedFilters) ? " has-active-filters" : ""}`}
              aria-label="Filter orders"
              title="Filter orders"
              onClick={() => setIsFilterPanelOpen(true)}
            >
              <FilterFunnelIcon width={16} height={16} color="#DEDEE2" />
            </button>
          )}
          <div className="orders-sort-control">
            <button
              type="button"
              className="orders-icon-text-button"
              onClick={handleRightButtonClick}
            >
              <SortIcon width={16} height={16} color="#ffffff" />
              {selectedPriority}
              <NavDownArrow width={16} height={16} color="#ffffff" />
            </button>
            {isDropdownOpen && (
              <div className="orders-priority-dropdown">
                {priorityOptions.map((option, index) => (
                  <div
                    key={index}
                    className={`orders-priority-option ${
                      !option.selectable ? "non-selectable" : ""
                    } ${option.label === selectedPriority ? "selected" : ""}`}
                    onClick={() => handlePrioritySelect(option)}
                  >
                    <span>{option.label}</span>
                    {option.label === selectedPriority && option.selectable && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.3333 4L6 11.3333L2.66667 8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {
            <button
              type="button"
              className="orders-icon-button"
              aria-label="Export orders"
              title="Export orders"
              onClick={handleExportOrders}
              disabled={isLoading || !hasOrdersToExport}
            >
              <ExportIcon width={16} height={16} color="#DEDEE2" />
            </button>
          }
        </div>
      </div>
      <div className="orders-dashboard-content">
        <div className="orders-dashboard-content-header">
          <button
            className={`dashboard-filter-button ${activeFilter === "overdue" ? "active" : ""}`}
            onClick={() => handleFilterClick("overdue")}
            disabled={filterCounts.overdue === 0}
          >
            {ORDER_FILTERS.OVERDUE} ({filterCounts.overdue})
          </button>
          <button
            className={`dashboard-filter-button ${activeFilter === "dueThisWeek" ? "active" : ""}`}
            onClick={() => handleFilterClick("dueThisWeek")}
            disabled={filterCounts.dueThisWeek === 0}
          >
            {ORDER_FILTERS.DUE_THIS_WEEK} ({filterCounts.dueThisWeek})
          </button>
          <button
            className={`dashboard-filter-button ${activeFilter === "dataTriage" ? "active" : ""}`}
            onClick={() => handleFilterClick("dataTriage")}
            disabled={filterCounts.dataTriage === 0}
          >
            {ORDER_FILTERS.DATA_TRIAGE} ({filterCounts.dataTriage})
          </button>
          <button
            className={`dashboard-filter-button ${activeFilter === "dataTransformation" ? "active" : ""}`}
            onClick={() => handleFilterClick("dataTransformation")}
            disabled={filterCounts.dataTransformation === 0}
          >
            {ORDER_FILTERS.DATA_TRANSFORMATION} (
            {filterCounts.dataTransformation})
          </button>
          <button
            className={`dashboard-filter-button ${activeFilter === "dataLoading" ? "active" : ""}`}
            onClick={() => handleFilterClick("dataLoading")}
            disabled={filterCounts.dataLoading === 0}
          >
            {ORDER_FILTERS.DATA_LOADING} ({filterCounts.dataLoading})
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="orders-loading-container">
            <LoadingIndicator show={true} fullscreen={false} />
          </div>
        )}

        {/* Error State */}
        {!isLoading && error && (
          <div className="orders-empty-state">
            <p className="orders-error-message">{error}</p>
          </div>
        )}

        {/* Empty State for Open Orders */}
        {!isLoading &&
          !error &&
          activeButton === "openOrders" &&
          sortedOrders.length === 0 && (
            <div className="orders-empty-state">
              <p className="orders-empty-message">{ORDERS.NO_OPEN_ORDERS}</p>
            </div>
          )}

        {/* Empty State for Action Needed */}
        {!isLoading &&
          !error &&
          activeButton === "actionNeeded" &&
          sortedOrders.length === 0 && (
            <div className="orders-empty-state">
              <p className="orders-empty-message">{ORDERS.NO_ACTION_NEEDED}</p>
            </div>
          )}

        {/* Content based on active button can be rendered here */}
        {!isLoading &&
          !error &&
          activeButton === "openOrders" &&
          sortedOrders.length > 0 && (
            <div className={isFullWidth ? "orders-grid-two-columns" : ""}>
              {/* Render open orders content here */}
              {sortedOrders.map((order) => (
                <div
                  key={order.axonId}
                  className="order-item"
                  onClick={() => handleOrderClick(order, "openOrders")}
                >
                  <div className="order-item-header">
                    <h6>{order.priority || "-"}</h6>
                    <h4>
                      AX-{order.axonId} |{" "}
                      {renderWithTooltip(
                        order.engagementName +
                          (order.clientEntity?.toLowerCase() !== "to be updated"
                            ? " - " + order.clientEntity
                            : ""),
                      )}{" "}
                      | {renderWithTooltip(order.analyzer)} |{" "}
                      {renderWithTooltip(order.engagementCode)}
                    </h4>
                  </div>
                  <div className="order-item-details">
                    <p className="order-item-details-status">
                      {order.orderActivityType === "Active"
                        ? order.status
                        : order.orderActivityType}
                      {isOutlookAgent(order) && (
                        <span className="outlook-agent-icon">
                          <OutlookplusIcon />
                        </span>
                      )}
                    </p>
                    <div className="order-item-dates">
                      {parseInt(order.agingIn.split("d")[0], 10) >= 2 && (
                        <CustomFieldsTooltip
                          trigger={
                            <p>
                              <CalendarIcon
                                width="16"
                                height="16"
                                color="#A4A3B1"
                              />
                              {order.agingIn}
                            </p>
                          }
                          agingDays={order.agingIn}
                          dueDate={formatDate(order.dueDate)}
                          agingLabel="Ageing in (By Status)"
                        />
                      )}
                      <CustomFieldsTooltip
                        trigger={
                          <p>
                            <ClockIcon width="16" height="16" color="#A4A3B1" />
                            {formatDate(order.dueDate)}
                          </p>
                        }
                        agingDays={order.agingIn}
                        dueDate={formatDate(order.dueDate)}
                        agingLabel="Due Date"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        {!isLoading &&
          !error &&
          activeButton === "actionNeeded" &&
          sortedOrders.length > 0 && (
            <div className={isFullWidth ? "orders-grid-two-columns" : ""}>
              {/* Render action needed orders content here */}
              {sortedOrders.map((order) => (
                <div
                  key={order.axonId}
                  className="order-item"
                  onClick={() => handleOrderClick(order, "actionNeeded")}
                >
                  <div className="order-item-header">
                    <h6>{order.priority || "-"}</h6>
                    <h4>
                      AX-{order.axonId} |{" "}
                      {renderWithTooltip(order.clientEntity)} |{" "}
                      {renderWithTooltip(order.analyzer)}
                    </h4>
                  </div>
                  <div className="order-item-details">
                    <p className="order-item-details-status">
                      {order.orderActivityType === "Active"
                        ? order.status
                        : order.orderActivityType}
                    </p>
                    <div className="order-item-dates">
                      {parseInt(order.agingIn.split("d")[0], 10) >= 2 && (
                        <CustomFieldsTooltip
                          trigger={
                            <p>
                              <CalendarIcon
                                width="16"
                                height="16"
                                color="#A4A3B1"
                              />
                              {order.agingIn}
                            </p>
                          }
                          agingDays={order.agingIn}
                          dueDate={formatDate(order.dueDate)}
                          agingLabel="Ageing in (By Status)"
                        />
                      )}
                      <CustomFieldsTooltip
                        trigger={
                          <p>
                            <ClockIcon width="16" height="16" color="#A4A3B1" />
                            {formatDate(order.dueDate)}
                          </p>
                        }
                        agingDays={order.agingIn || "-"}
                        dueDate={order.createdDate}
                        agingLabel="Due Date"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
};
